/* eslint-disable jsx-a11y/no-autofocus */
import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { IconClose } from '@medifind/icons';
import { SearchSuggestion } from '@medifind/shared-basic-components';
import { Autocomplete } from '@medifind/shared-basic-components';
import { areFilterStatesEqual } from '@medifind/zustand';
import { Filter } from './Filter';
import styles from './FilterShared.module.scss';

const StoreFilterAutocomplete = ({
  onSelect,
  filter,
  placeholder,
  label,
  classes = {},
  what,
  className,
  actLikeASelect = true,
  clearInputAfterSelect,
  childrenSuggestion,
  smallAt,
  fullWidth,
  defaultOptions = [],
  prepopulateOnFocus,
  autocompleteFunction,
  onClear,
  showClearButton,
  showIsRequiredMessage,
  noResultsString,
  noHeader,
  onlyAllowOneSelection = false,
}) => {
  const { selected, getInitialState, reset, setFilterValue, type } = filter;
  const isSingleSelect = type === 'singleSelect';
  const [suggestions, setSuggestions] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const getInitialSuggestions = () => {
    autocompleteFunction('')
      .then((data) => {
        setSuggestions(data);
      })
      .catch(() => null);
  };
  const handleClearSelect = () => {
    if (!actLikeASelect) {
      setSuggestions([]);
    }
    setAutocompleteValue('');
    reset && reset();
    onClear && onClear();
  };
  useEffect(() => {
    if (actLikeASelect && !prepopulateOnFocus) getInitialSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (autocompleteValue != null || (suggestions.length > 0 && !filter?.selected)) {
      autocompleteFunction(autocompleteValue)
        .then(setSuggestions)
        .catch(() => null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autocompleteValue, filter?.selected]);
  const getDisplay = () => {
    if (isSingleSelect && selected?.display) return selected.display;
    if (!isSingleSelect && selected?.[0]?.display && onlyAllowOneSelection) return selected[0].display;
  };

  const items = useMemo(() => {
    if (typeof suggestions === 'string') return suggestions;
    else return [...defaultOptions, ...(suggestions ?? [])];
  }, [suggestions]);
  return (
    <Filter
      classes={classes}
      label={label}
      what={what}
      smallAt={smallAt}
      noHeader={noHeader}
      placeholder={getDisplay() ?? placeholder}
      onClear={handleClearSelect}
      isChanged={!areFilterStatesEqual(getInitialState(), filter)}
    >
      <Autocomplete
        onFocus={prepopulateOnFocus && suggestions.length < 1 ? getInitialSuggestions : null}
        label={label}
        labelId={'-'} // Is replaced by <Filter>
        hideLabel
        items={items}
        inputValue={getDisplay() ?? ''}
        inputProp={filter}
        placeholder={placeholder}
        inputIcon={
          showClearButton && getDisplay()
            ? [
                {
                  icon: <IconClose size="medium" />,
                  onClick: handleClearSelect,
                  label: 'Clear ' + (label ?? 'Field'),
                },
              ]
            : null
        }
        noResultsString={noResultsString}
        onChange={(value) => {
          setAutocompleteValue(value ?? '');
        }}
        onClear={handleClearSelect}
        onSelect={(selected, ref) => {
          if (selected == null) return;
          const { value, display, accessRequest } = selected;
          setFilterValue &&
            setFilterValue(
              isSingleSelect || !onlyAllowOneSelection ? { value, display, accessRequest } : [{ value, display, accessRequest }],
            );
          onSelect && onSelect({ value, display, accessRequest });
        }}
        onEnter={(value, { highlightedIndex }) => {
          if (suggestions.length == 0 || typeof suggestions === 'string') {
            event.nativeEvent.preventDownshiftDefault = true;
          }
        }}
        actLikeASelect={actLikeASelect}
        clearInputAfterSelect={clearInputAfterSelect}
        activeFirstOption={true}
        className={classNames(className, {
          [styles['full-width']]: fullWidth,
        })}
        classes={{
          menuWrapper: classNames(styles['condition-search__menu-wrapper'], classes.menuWrapper),
          inputWrapper: classNames(styles['condition-search__input-wrapper'], styles['filter-input-wrapper'], classes.inputWrapper),
          menu: classNames(classes.menu),
          menuItem: classNames(styles['text'], classes.menuItem),
          validMenuItem: styles['arrow'],
          menuList: classNames(classes.menuList),
          input: classNames(classes.input),
        }}
        childrenSuggestion={
          childrenSuggestion ??
          ((item) =>
            item.key === 'doctors' ? (
              <SearchSuggestion suggestion={item} />
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: item.highlight ?? item.display,
                }}
              ></span>
            ))
        }
      />
      {showIsRequiredMessage && !selected && <div className={styles['error-message__filter']}>{label} is Required</div>}
    </Filter>
  );
};

StoreFilterAutocomplete.propTypes = {
  actLikeASelect: PropTypes.bool,
  filter: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  fullWidth: PropTypes.bool,
  prepopulateOnFocus: PropTypes.bool,
  defaultOptions: PropTypes.array,
  onSelect: PropTypes.func,
  what: PropTypes.node,
  className: PropTypes.string,
  classes: PropTypes.object,
  smallAt: PropTypes.string,
  autocompleteFunction: PropTypes.func,
  onClear: PropTypes.func,
  childrenSuggestion: PropTypes.func,
  showClearButton: PropTypes.bool,
  onlyAllowOneSelection: PropTypes.bool,
};

export { StoreFilterAutocomplete as default, StoreFilterAutocomplete };
